import React, { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";
import { Globe, Copy, CheckCircle2, Clock, Loader2, RefreshCw, AlertCircle, Trash2, ShieldCheck, ExternalLink } from "lucide-react";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { DOMAIN_RE } from "../lib/validation";
import { apiErrorCode } from "../lib/apiErrors";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";
import CapabilityNotice from "./entitlements/CapabilityNotice";

// DOMAIN_RE is the ONE shared format rule (lib/validation.js — same constant
// SiteBuilderWizard.jsx's address step imports, not a lookalike copy). Unlike that
// wizard, THIS page treats the domain as required (see `min(1, "dom.enterDomain")` below).
const DOMAIN_SCHEMA = z.object({
  domain: z.string().trim().min(1, "dom.enterDomain"),
}).superRefine((val, ctx) => {
  const d = val.domain.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
  if (d && !DOMAIN_RE.test(d)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["domain"], message: "customDomain.invalidFormat" });
  }
});

// The five statuses of backend/modules/custom_domains, plus "none" (no domain set).
const BADGE = {
  none: { cls: "bg-eh-cream text-eh-muted", Icon: null },
  pending_dns: { cls: "bg-amber-100 text-amber-800", Icon: Clock },
  provisioning: { cls: "bg-sky-100 text-sky-800", Icon: Loader2 },
  live: { cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  failed: { cls: "bg-rose-100 text-rose-800", Icon: AlertCircle },
  lapsed: { cls: "bg-amber-100 text-amber-800", Icon: AlertCircle },
};

const DnsRow = ({ type, host, value, t }) => (
  <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center bg-eh-cream/40 rounded-lg px-3 py-2 text-sm">
    <span className="text-xs font-semibold uppercase text-eh-muted w-14">{type}</span>
    <div className="min-w-0">
      <div className="text-[11px] text-eh-muted">{t("dom.hostName")}</div>
      <code className="block truncate text-eh-ink">{host}</code>
      <div className="text-[11px] text-eh-muted mt-1">{t("dom.valueTarget")}</div>
      <code className="block truncate text-eh-ink">{value}</code>
    </div>
    <button onClick={() => navigator.clipboard.writeText(value).then(() => toast.success(t("dom.copied"))).catch(() => {})} className="p-2 hover:bg-white rounded" title={t("dom.copyValue")}><Copy className="w-4 h-4" /></button>
  </div>
);

/** What the last DNS inspection found wrong, one line per code (server `findings.codes`). */
const Findings = ({ findings, cnameTarget, t }) => {
  const codes = findings?.codes || [];
  if (!codes.length) return null;
  const seen = [findings.cname_found, ...(findings.a_found || []), ...(findings.txt_found || [])].filter(Boolean);
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2" data-testid="domain-verify-feedback">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="space-y-1">
        <div className="font-semibold">{t("cdom.findingsTitle")}</div>
        {codes.map((c) => <div key={c} data-testid={`domain-finding-${c}`}>{t(`cdom.code.${c}`, { target: cnameTarget })}</div>)}
        {seen.length > 0 && <div>{t("cdom.found", { value: seen.join(", ") })}</div>}
      </div>
    </div>
  );
};

/**
 * CustomDomainAdmin — connect the practice's own domain (backend/modules/custom_domains).
 *
 * The page follows the domain's status: the DNS records to add while it waits for DNS, a
 * "securing" note while the certificate is issued, the live address once it is live, the
 * reason and a Retry when it failed, the deadline when it lapsed. Every action answers
 * with the server's view of the domain, which the page renders as is.
 */
export default function CustomDomainAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [info, setInfo] = useState(null);
  const [domainInput, setDomainInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [edgeError, setEdgeError] = useState(null);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  // The page is never route-gated: a practice whose plan no longer includes a custom
  // domain still sees the domain it has and its status. What `site.custom_domain`
  // withholds is SETTING and VERIFYING one — those controls give way to the plan notice.
  // `null` (map not known yet) claims nothing.
  const ent = useEntitlements();
  const domainState = ent.status(CAP.customDomain);
  const canConfigure = domainState !== "not_in_plan" && domainState !== "coming_soon";

  const show = useCallback((data) => {
    setInfo(data);
    setDomainInput(data.domain || "");
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/custom-domain");
      show(data);
    } catch (e) {
      toast.error(extractApiError(e, t("dom.error")));
    }
  }, [t, show]);

  useEffect(() => { load(); }, [load]);

  /** A refusal the server named with a code this page translates, else the generic text. */
  const refusal = (e) => {
    const code = apiErrorCode(e);
    const known = code ? t(`cdom.err.${code}`, null, "") : "";
    return known || extractApiError(e, t("dom.error"), tc);
  };

  const saveDomain = async () => {
    if (validate(DOMAIN_SCHEMA, { domain: domainInput })) { toast.error(tc("validation.fixHighlighted")); return; }
    const d = domainInput.trim().toLowerCase();
    setSaving(true);
    try {
      const { data } = await api.post("/tenant/custom-domain", { domain: d });
      setEdgeError(null);
      show(data);
      toast.success(t("dom.saved"));
    } catch (e) {
      toast.error(refusal(e));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Take the domain down: `POST /tenant/custom-domain {"domain": ""}` clears it. Unlike
   * setting one, a CLEAR does not require `site.custom_domain`, so this is offered with
   * or without the capability — a downgraded practice must be able to remove its domain.
   * Confirmed first: the public site stops answering on that domain at once.
   */
  const removeDomain = async () => {
    if (!window.confirm(t("dom.removeConfirm", { domain: info.domain }))) return;
    setRemoving(true);
    try {
      const { data } = await api.post("/tenant/custom-domain", { domain: "" });
      setEdgeError(null);
      show(data);
      toast.success(t("dom.removed"));
    } catch (e) {
      toast.error(refusal(e));
    } finally {
      setRemoving(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/tenant/custom-domain/verify");
      setEdgeError(data.edge_error || null);
      show(data);
    } catch (e) {
      toast.error(refusal(e));
    } finally {
      setVerifying(false);
    }
  };

  if (!info) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>;

  const status = info.status || "none";
  const badge = BADGE[status] || BADGE.none;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(dateLocale(lang), { dateStyle: "long" }) : "");
  const showRecords = canConfigure && info.domain && status !== "live" && status !== "provisioning";
  const verifyLabel = status === "failed" ? t("cdom.retry") : status === "pending_dns" ? t("cdom.check") : t("cdom.checkNow");

  return (
    <div data-testid="custom-domain-admin" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3"><Globe className="w-6 h-6 text-sol-gold" /> {t("dom.title")}</h1>
        <p className="text-eh-muted text-sm mt-1">{t("dom.subtitle")}</p>
      </div>

      <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl">{t("dom.yourDomain")}</h3>
          <span className={`text-xs px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${badge.cls}`} data-testid={`domain-status-${status}`}>
            {badge.Icon && <badge.Icon className={`w-3 h-3 ${status === "provisioning" ? "animate-spin" : ""}`} />} {t(`cdom.status.${status}`)}
          </span>
        </div>
        {!canConfigure && info.domain && (
          <code className="block text-eh-ink" data-testid="domain-current">{info.domain}</code>
        )}
        {info.domain && (
          <Button
            variant="outline"
            onClick={removeDomain}
            disabled={removing}
            data-testid="domain-remove"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
          >
            {removing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            {t("dom.remove")}
          </Button>
        )}
        <CapabilityNotice
          state={domainState}
          eta={ent.eta(CAP.customDomain)}
          body={t("dom.planNotice")}
          testId="domain-plan-notice"
        />
        {canConfigure && (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">{t("dom.domain")}</Label>
              <Input
                value={domainInput}
                onChange={(e) => { setDomainInput(e.target.value); clearFieldError("domain"); }}
                placeholder={t("cdom.placeholder")}
                data-testid="domain-input"
                className={`mt-1.5 ${fieldErrors.domain ? invalidInputClass : ""}`}
              />
              <FieldError message={fieldErrors.domain ? t(fieldErrors.domain) : ""} testId="domain-input-error" />
            </div>
            <Button onClick={saveDomain} disabled={saving} data-testid="domain-save" className="bg-eh-ink text-eh-bg">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {t("dom.save")}
            </Button>
          </div>
        )}
      </div>

      {info.domain && (
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-4">
          {status === "live" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 flex gap-2" data-testid="domain-live">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div>
                  {t("cdom.live")}{" "}
                  <a href={info.live_url} target="_blank" rel="noopener noreferrer" className="font-semibold underline inline-flex items-center gap-1">
                    {info.live_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="mt-1 text-emerald-800">{t("cdom.liveNote")}</div>
              </div>
            </div>
          )}
          {status === "provisioning" && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900 flex gap-2" data-testid="domain-provisioning">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{t("cdom.provisioning", { domain: info.domain })}</div>
            </div>
          )}
          {status === "failed" && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900 flex gap-2" data-testid="domain-failed">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{t("cdom.failed", { domain: info.domain })}</div>
                {info.failure?.code && <div className="mt-1" data-testid="domain-failure-reason">{t(`cdom.code.${info.failure.code}`)}</div>}
                <div className="mt-1">{t("cdom.failedHint")}</div>
              </div>
            </div>
          )}
          {status === "lapsed" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex gap-2" data-testid="domain-lapsed">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div>{t("cdom.lapsed", { domain: info.domain, since: fmtDate(info.lapsed_at) })}</div>
                {info.release_at && <div className="mt-1 font-semibold">{t("cdom.lapsedRelease", { date: fmtDate(info.release_at) })}</div>}
              </div>
            </div>
          )}

          {showRecords && (
            <>
              <h3 className="font-display text-xl">{t("dom.dnsSteps")}</h3>
              <ol className="text-sm text-eh-muted space-y-2 list-decimal list-inside">
                <li>{t("dom.step1")}</li>
                <li>{t("cdom.stepAdd")}</li>
              </ol>
              {info.kind === "apex" && <p className="text-xs text-eh-muted" data-testid="domain-apex-note">{t("cdom.apexNote")}</p>}
              <div className="space-y-2" data-testid="dns-records">
                {(info.records || []).map((r) => <DnsRow key={`${r.type}-${r.value}`} type={r.type} host={r.host} value={r.value} t={t} />)}
              </div>
            </>
          )}

          {(status === "pending_dns" || status === "lapsed") && (
            <Findings findings={info.findings} cnameTarget={info.cname_target} t={t} />
          )}

          {(status === "pending_dns" || status === "failed") && info.release_at && (
            <p className="text-xs text-eh-muted" data-testid="domain-unproven-release">{t("cdom.unprovenRelease", { date: fmtDate(info.release_at) })}</p>
          )}

          {edgeError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2" data-testid="domain-edge-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{t("cdom.edgeError")}</div>
            </div>
          )}

          {canConfigure && (
            <div className="flex items-center gap-3">
              <Button onClick={verify} disabled={verifying} data-testid="domain-verify" className="bg-sol-gold text-eh-ink hover:bg-sol-gold/90">
                {verifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} {verifyLabel}
              </Button>
              {info.checked_at && <span className="text-xs text-eh-muted">{t("dom.lastCheck")} {new Date(info.checked_at).toLocaleString(dateLocale(lang))}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
